import React from 'react'
import { LinkProps } from 'react-router-dom'
import { Formik, FormikProps } from 'formik'
import * as yup from 'yup'

// Constants
import { routes } from 'constants/routes'

// Components
import Button from 'components/Button'
import { Flex } from 'components/Box'
import Form from 'components/Form'
import UnTypedInput from 'components/Input'
import PasswordInput from 'components/PasswordInput'
import Link from 'components/Link'

// Types
import { useSignIn } from 'modules/user/operations'
import { ParsedError } from 'graphql/apollo/useMutation'

const Input = UnTypedInput as any

type Values = typeof SIGN_IN_INITIAL_VALUES

export interface ExtendedFormikProps extends FormikProps<Values> {
  apiErrors?: ParsedError
}

export interface FieldsProps extends ExtendedFormikProps {
  hideForgotPasswordLink?: boolean
  forgotPasswordLink?: LinkProps['to']
}

// Helpers
const SIGN_IN_INITIAL_VALUES = {
  email: '',
  password: '',
}

const signInSchema = yup.object().shape({
  email: yup
    .string()
    .email('This is not a valid email')
    .required('Email can’t be blank'),
  password: yup.string().required('Password can’t be blank'),
})

export const Fields: React.FC<FieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  hideForgotPasswordLink,
  forgotPasswordLink,
}) => (
  <>
    <Form.Field
      name="text"
      label="Email"
      errorText={touched.email ? errors.email : undefined}>
      <Input
        name="email"
        type="email"
        placeholder="john@appleseed.com"
        value={values.email}
        onChange={handleChange}
        onBlur={handleBlur}
        autoFocus
      />
    </Form.Field>
    <Form.Field
      name="password"
      errorText={touched.password ? errors.password : undefined}
      label={
        <Flex justifyContent="space-between" alignItems="center">
          Password
          {!hideForgotPasswordLink && (
            <Link
              className="FormLabel__aside"
              variant="secondary"
              isUnderlined
              tabIndex={-1}
              to={forgotPasswordLink || routes.FORGOT_PASSWORD.create({})}>
              Forgot Password?
            </Link>
          )}
        </Flex>
      }>
      <PasswordInput
        name="password"
        placeholder="••••••••"
        value={values.password}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </Form.Field>
  </>
)

interface SubmitProps {
  isSubmitting: boolean
}

export const Submit: React.FC<SubmitProps> = ({ isSubmitting }) => (
  <Form.Footer>
    <Button
      variant="primary-untinted"
      type="submit"
      fill
      small
      disabled={isSubmitting}
      loading={isSubmitting}>
      Sign In
    </Button>
  </Form.Footer>
)

export const Errors: React.FC<ExtendedFormikProps> = ({ apiErrors }) =>
  apiErrors?.message ? (
    <Form.ErrorField>{apiErrors.message}</Form.ErrorField>
  ) : null

export const DefaultFormFields: React.FC<ExtendedFormikProps> = formProps => (
  <>
    <Fields {...formProps} />
    <Errors {...formProps} />
    <Submit isSubmitting={formProps.isSubmitting} />
  </>
)

interface SignInFormProps {
  email?: string
  children?: (formikProps: ExtendedFormikProps) => React.ReactNode
  onCompleted?: () => void
}

const SignInForm: React.FC<SignInFormProps> = ({
  children = DefaultFormFields,
  email = '',
  onCompleted,
}) => {
  const [signIn, { loading, error }] = useSignIn({
    onCompleted,
  })

  return (
    <Formik
      initialValues={{ ...SIGN_IN_INITIAL_VALUES, email }}
      onSubmit={async ({ email, password }) => {
        try {
          await signIn({ variables: { email, password } })
        } catch (e) {
          // The error handling should be taken care of by Apollo
        }
      }}
      validationSchema={signInSchema}
      validateOnBlur={false}>
      {formikBag => (
        <Form>
          {children({
            ...formikBag,
            isSubmitting: loading,
            apiErrors: error,
          })}
        </Form>
      )}
    </Formik>
  )
}

export default SignInForm
